import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { COLLECTIONS } from '../data/collections';
import { DELIVERY, OFFER, BUSINESS, EXCHANGE, TOP_SELLERS, DISCOUNT_COUNT } from '../data/pricing';
import { formatINR } from '../lib/quote';
import { listVariants, filterVariants, byStock } from '../lib/listing';
import ProductCard from '../components/ProductCard';
import ListingFilterBar from '../components/ListingFilterBar';
import { track, FUNNEL } from '../lib/track';
import CredentialRibbon from '../components/CredentialRibbon';
import CompareBar from '../components/CompareBar';
import HelpChoosing, { LIVE_GUIDE_SLUGS } from '../components/HelpChoosing';
import { helpForPriceList } from '../data/helpChoosing';
import { useTray, toggleTray } from '../lib/compareTray';
import { MAX_COMPARE } from '../data/compare';
import { DiwaliLights, DiwaliFireworks } from '../components/DiwaliFestive';
import WelcomeOffer from '../components/WelcomeOffer';


// Route: /daikin-ac-price-list-delhi
//
// Lists variants rather than models, so a unit that exists with and without
// the piping kit appears twice at its two real prices, and the kit filter
// separates them. Quantity and add-to-cart sit on the card — someone buying
// three of the same unit never needs to open the product page.
//
// Filters, sort and the kit explainer live in ListingFilterBar. On a phone
// they collapse to one row plus a bottom sheet, because three wrapped chip
// rows and an explanatory paragraph filled the entire first screen and the
// page read as an article rather than a shop.

// A merchandising strip: your curated picks, scrolling sideways on a phone.
// Deliberately not shaped like the catalogue grid below — that difference is
// what stops a unit appearing in both places from reading as a duplicate.
function Strip({ title, note, items, compare }) {
  if (!items.length) return null;
  return (
    <section className="mt-8">
      <h2 className="text-base font-semibold text-gray-900 sm:text-lg">{title}</h2>
      <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">{note}</p>
      <div className="strip-scroll -mx-4 mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 lg:grid-cols-4">
        {items.map((v, i) => (
          <div key={v.key} className="w-[158px] shrink-0 snap-start sm:w-auto [&>*]:h-full">
            <ProductCard variant={v} index={i} compare={compare} />
          </div>
        ))}
      </div>
    </section>
  );
}

// A catalogue band. On a phone only the first few cards show, because eleven
// cards two-up is eleven screens of scrolling before anyone reaches whatever
// comes next. Desktop always shows the lot.
function Band({ title, note, items, initial = 4, compare }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <section className="mt-8">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900 sm:text-lg">{title}</h2>
        <span className="shrink-0 text-xs text-gray-500">
          {items.length} unit{items.length === 1 ? '' : 's'}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">{note}</p>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((v, i) => (
          <div key={v.key} className={`[&>*]:h-full ${!open && i >= initial ? 'hidden sm:block' : ''}`}>
            <ProductCard variant={v} index={i} compare={compare} />
          </div>
        ))}
      </div>
      {items.length > initial && (
        <button type="button" onClick={() => setOpen(!open)}
          className="mt-3 w-full rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-blue-700 sm:hidden">
          {open ? 'Show less' : `Show ${items.length - initial} more`}
        </button>
      )}
    </section>
  );
}

export default function DaikinPrices() {
  const [ton, setTon] = useState('all');
  const [star, setStar] = useState('all');
  const [kit, setKit] = useState('all');
  const [inverterOnly, setInverterOnly] = useState(false);
  const [sort, setSort] = useState('price_asc');

  useEffect(() => { track(FUNNEL.PRICE_PAGE_VIEW, {}); }, []);

  const all = useMemo(() => listVariants(), []);
  const trayIds = useTray();
  const compare = { ids: trayIds, toggle: toggleTray, full: trayIds.length >= MAX_COMPARE };
  const SORTS = {
    price_asc: { label: 'Price: low to high', fn: (a, b) => a.price - b.price },
    price_desc: { label: 'Price: high to low', fn: (a, b) => b.price - a.price },
    discount: { label: 'Biggest saving', fn: (a, b) => (b.mrp - b.price) - (a.mrp - a.price) },
    efficient: { label: 'Most efficient', fn: (a, b) => b.model.iseer - a.model.iseer },
    capacity: { label: 'Capacity', fn: (a, b) => a.model.kw - b.model.kw },
  };

  const list = useMemo(
    () => [...filterVariants(all, { ton, star, kit, inverterOnly })].sort((a, b) => byStock(a, b) || SORTS[sort].fn(a, b)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, ton, star, kit, inverterOnly, sort],
  );

  const stocked = useMemo(() => list.filter((v) => v.inStock), [list]);
  const onOrder = useMemo(() => list.filter((v) => !v.inStock), [list]);

  // Your picks, in your order. Drawn from the filtered list so a filter that
  // hides a top seller does not bring it back in its own band.
  const topSellers = useMemo(
    // Matched on the model, not the kit: the unit sold can switch to the
    // with-kit one when that is what is in stock.
    () => TOP_SELLERS.map((k) => list.find((v) => v.model.id === k.split(':')[0])).filter(Boolean),
    [list],
  );

  // Biggest rupee savings against Daikin's own MRP. By percentage this band
  // would be a near-copy of the in-stock band, since the old-stock units lead
  // on both counts. Sold-out units stay out of it: a deal you cannot have is
  // not a deal. They still show in Top sellers, where "sold out" is the point.
  const discounts = useMemo(
    () => list.filter((v) => !v.soldOut).sort((a, b) => (b.mrp - b.price) - (a.mrp - a.price)).slice(0, DISCOUNT_COUNT),
    [list],
  );

  // "What size do I need" is the first question every AC buyer has. Answer it
  // before showing them nineteen cards.
  const sizes = useMemo(() => ['1.0', '1.5', '1.8', '2.0'].map((t) => {
    const c = COLLECTIONS.find((x) => x.filter && x.filter.soldAs === t);
    // Sold-out units are left out: a "from" price nobody can buy at is not one.
    const prices = all.filter((v) => v.model.soldAs === t && !v.soldOut).map((v) => v.price);
    return c && prices.length ? { ton: t, slug: c.slug, from: Math.min(...prices) } : null;
  }).filter(Boolean), [all]);

  const bestExchange = Math.max(...EXCHANGE.copper.map((c) => c.value));

  return (
    <>
      <Helmet>
        <title>Buy Daikin AC Online Delhi NCR | Authorised Dealer | Citi Cool</title>
        <meta name="description"
          content="Buy Daikin split ACs online in Delhi, Noida, Gurgaon and Faridabad. 0.8 to 2.5 ton, 2 to 5 star, every price on the page including GST. 24-hour delivery on stocked units. Cooling Delhi since 1990. Authorised Daikin dealer since 2003." />
      </Helmet>

      <main className="mx-auto max-w-6xl px-4 pt-20 pb-16 lg:pt-24">
        <div className="mb-4 overflow-hidden rounded-xl">
          <CredentialRibbon items={[
            "Authorized Daikin dealer",
            "35+ Years experience",
            "Awarded #1 HVAC Dealer in North India",
            "MD Club Gold Member",
            "50,000+ tonnes installed"
          ]} />
        </div>
        {/* Category banner, Vijay Sales pattern: inside the content column
            rather than full bleed, rounded, above the heading. Not a link —
            it sits on the page it would send you to. */}
        {/* Diwali lights hang across the top of the banner until 10 Nov. */}
        <div className="relative">
        <picture className="mb-4 block w-full overflow-hidden rounded-xl sm:mb-6">
          <img
            src="/banner-daikin-desktop.webp"
            srcSet="/banner-daikin-desktop-900.webp 900w, /banner-daikin-desktop-1400.webp 1400w, /banner-daikin-desktop.webp 2172w"
            sizes="(min-width: 1152px) 1120px, 100vw"
            alt="Buy Daikin ACs at best prices from an authorized dealer in Delhi. 100% authentic products, fast delivery, expert installation."
            width="2172"
            height="724"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full"
          />
        </picture>
        <DiwaliLights />
        </div>


        <h1 className="text-xl font-semibold text-gray-900 sm:text-3xl">Buy Daikin AC online</h1>
        <p className="mt-1 text-xs text-gray-600 sm:mt-2 sm:text-sm">
          Cooling Delhi since {BUSINESS.since} · Authorised Daikin dealer since {BUSINESS.daikinSince} · {BUSINESS.serviceAreas.join(', ')}
        </p>

        {sizes.length > 0 && (
          <nav aria-label="Shop by size"
            className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
            {sizes.map((s) => (
              <Link key={s.ton} to={`/${s.slug}`}
                className="shrink-0 rounded-full border border-gray-300 px-3.5 py-2 text-xs text-gray-900 transition hover:border-blue-500 hover:text-blue-700">
                {s.ton} Ton <span className="text-gray-500">from {formatINR(s.from)}</span>
              </Link>
            ))}
          </nav>
        )}

        {OFFER.active && OFFER.note && (
          <div className="mt-3 rounded-lg bg-blue-50 px-4 py-2.5 text-xs text-blue-900 sm:mt-4 sm:text-sm">
            <span className="font-medium">{OFFER.headline}</span> — {OFFER.note}
          </div>
        )}

        <ListingFilterBar
          ton={ton} setTon={setTon}
          star={star} setStar={setStar}
          kit={kit} setKit={setKit}
          inverterOnly={inverterOnly} setInverterOnly={setInverterOnly}
          sort={sort} setSort={setSort}
          sorts={SORTS}
          count={list.length}
        />

        {list.length === 0 ? (
          <p className="mt-12 text-center text-sm text-gray-500">Nothing matches those filters.</p>
        ) : (
          <>
            <Strip
              title="Top sellers"
              note="What Delhi NCR keeps buying from us this season."
              items={topSellers} compare={compare} />

            <Band
              title="Delivered tomorrow"
              note="In our godown right now. Order today, at your door on the next working day."
              items={stocked} compare={compare} />

            <Strip
              title="Great discounts"
              note="The biggest savings against Daikin's own MRP today."
              items={discounts} compare={compare} />

            {EXCHANGE.enabled && (
              <section className="mt-8 rounded-xl bg-green-50 px-4 py-3.5 sm:px-5 sm:py-4">
                <p className="text-sm font-medium text-green-900 sm:text-base">
                  Up to {formatINR(bestExchange)} off your old AC
                </p>
                <p className="mt-0.5 text-xs text-green-800 sm:text-sm">
                  Any brand. Free uninstallation. Applied at checkout.
                </p>
              </section>
            )}

            <Band
              title="Everything else"
              note="Ordered from Daikin for you, delivered in 5 to 7 days."
              items={onOrder} compare={compare} />
          </>
        )}

        <p className="mt-6 text-xs text-gray-500">{DELIVERY.footnote}</p>

        <HelpChoosing links={helpForPriceList(LIVE_GUIDE_SLUGS)} className="mt-8 max-w-4xl" />

        <section className="mt-10 border-t border-gray-200 pt-6">
          <h2 className="text-sm font-medium text-gray-900">Browse by size and rating</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {COLLECTIONS.map((c) => (
              <Link key={c.slug} to={`/${c.slug}`}
                className="rounded-full border border-gray-300 px-3.5 py-1.5 text-xs text-gray-600 transition hover:border-blue-400 hover:text-blue-600">
                {c.h1.replace(' Price List', '').replace(' Price', '')}
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-8 rounded-xl bg-blue-600 px-6 py-6 text-center sm:px-10">
          <p className="text-base font-medium text-white">Buying more than six, or not sure which one?</p>
          <p className="mt-1 text-sm text-blue-100">Bulk pricing is different. Talk to us directly.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <a href={`tel:${BUSINESS.phone}`}
              onClick={() => track(FUNNEL.PHONE_CLICK, { source: 'price_page' })}
              className="rounded-md bg-white px-5 py-2.5 text-sm font-medium text-blue-700 transition hover:bg-blue-50">
              Call {BUSINESS.phoneDisplay}
            </a>
            <a href={`https://wa.me/${BUSINESS.whatsapp}`} target="_blank" rel="noopener noreferrer"
              onClick={() => track(FUNNEL.WHATSAPP_HANDOFF, { source: 'price_page' })}
              className="rounded-md border-2 border-white px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700">
              WhatsApp
            </a>
          </div>
        </div>
      </main>
      <CompareBar />
      <DiwaliFireworks />
      <WelcomeOffer />
    </>
  );
}